import { useState } from 'react'
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Avatar,
  Menu,
  MenuItem
} from '@mui/material'
import {
  Menu as MenuIcon,
  Dashboard,
  ConfirmationNumber,
  Add,
  Person,
  Queue as QueueIcon,
  Analytics,
  Assignment,
  People,
  Business,
  Settings,
  AdminPanelSettings,
  CreditCard,
  HelpOutline,
  TrendingUp,
  Assessment,
  Email
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@shared/auth/AuthContext'
import TrialStatusBanner from '@shared/components/TrialStatusBanner'
import { useSignalR } from '../hooks/useSignalR'
import { NotificationManager } from '../components/NotificationManager'

const drawerWidth = 240

interface NavigationItem {
  text: string
  icon: React.ReactElement
  path: string
  roles: string[]
}

const navigationItems: NavigationItem[] = [
  // Customer navigation (includes Oversight users)
  { text: 'Dashboard', icon: <Dashboard />, path: '/customer/dashboard', roles: ['Customer', 'Oversight'] },
  { text: 'My Tickets', icon: <ConfirmationNumber />, path: '/customer/tickets', roles: ['Customer', 'Oversight'] },
  { text: 'Create Ticket', icon: <Add />, path: '/customer/create-ticket', roles: ['Customer'] },
  { text: 'Analytics', icon: <Analytics />, path: '/customer/analytics', roles: ['Oversight'] },
  { text: 'SLA Analytics', icon: <Assessment />, path: '/admin/sla-escalation-analytics', roles: ['Oversight'] },
  { text: 'Profile', icon: <Person />, path: '/customer/profile', roles: ['Customer'] },
  { text: 'Help', icon: <HelpOutline />, path: '/customer/help', roles: ['Customer', 'Oversight'] },

  // Agent navigation
  { text: 'Dashboard', icon: <Dashboard />, path: '/agent/dashboard', roles: ['Agent'] },
  { text: 'Ticket Queue', icon: <QueueIcon />, path: '/agent/queue', roles: ['Agent'] },
  { text: 'My Tickets', icon: <Assignment />, path: '/agent/tickets', roles: ['Agent'] },
  { text: 'Analytics', icon: <Analytics />, path: '/agent/analytics', roles: ['Agent'] },
  { text: 'Help', icon: <HelpOutline />, path: '/agent/help', roles: ['Agent'] },

  // Admin navigation (Admin only)
  { text: 'Dashboard', icon: <AdminPanelSettings />, path: '/admin/dashboard', roles: ['Admin'] },
  { text: 'Tickets Overview', icon: <ConfirmationNumber />, path: '/admin/tickets', roles: ['Admin'] },
  { text: 'Analytics', icon: <Analytics />, path: '/admin/analytics', roles: ['Admin'] },
  { text: 'User Management', icon: <People />, path: '/admin/users', roles: ['Admin'] },
  { text: 'Organisations', icon: <Business />, path: '/admin/organizations', roles: ['Admin'] },
  { text: 'Billing', icon: <CreditCard />, path: '/admin/billing', roles: ['Admin'] },
  { text: 'Settings', icon: <Settings />, path: '/admin/settings', roles: ['Admin'] },
  { text: 'Email Templates', icon: <Email />, path: '/admin/email-templates', roles: ['Admin'] },
  { text: 'Escalation Paths', icon: <TrendingUp />, path: '/admin/escalation-paths', roles: ['Admin'] },
  { text: 'SLA Analytics', icon: <Assessment />, path: '/admin/sla-escalation-analytics', roles: ['Admin'] },
  { text: 'Help', icon: <HelpOutline />, path: '/admin/help', roles: ['Admin'] },
]

interface DashboardLayoutProps {
  children: React.ReactNode
  userType: 'customer' | 'agent' | 'admin'
}

function DashboardLayout({ children, userType }: DashboardLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, getAccessToken } = useAuth()

  // Initialize SignalR connection for real-time notifications
  useSignalR({
    enabled: !!user,
    accessToken: getAccessToken() || undefined
  })

  const filteredNavItems = navigationItems.filter(item =>
    item.roles.includes(user?.role || 'Customer')
  )

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleUserMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    handleUserMenuClose()
    logout()
  }

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          {userType === 'customer' ? 'Customer Portal' : userType === 'agent' ? 'Agent Portal' : 'Admin Portal'}
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {filteredNavItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  )

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Real-time browser notifications */}
      <NotificationManager />

      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Helpdesk Portal
          </Typography>

          <Button
            color="inherit"
            onClick={handleUserMenuOpen}
            startIcon={<Avatar sx={{ width: 32, height: 32 }}>{user?.firstName?.[0] || user?.email?.[0] || 'U'}</Avatar>}
          >
            {user?.firstName || user?.fullName || 'User'}
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
          >
            <MenuItem onClick={() => navigate('/customer/profile')}>Profile</MenuItem>
            <MenuItem onClick={handleLogout}>Sign Out</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8
        }}
      >
        {/* Show trial status banner for Admin users only */}
        {user?.role === 'Admin' && <TrialStatusBanner />}
        {children}
      </Box>
    </Box>
  )
}

export default DashboardLayout